import axios from "axios";

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
  timeout: 30000, // 30 seconds
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response } = error;

    if (response) {
      // Handle specific HTTP status codes
      switch (response.status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem("token");
          delete api.defaults.headers.common["Authorization"];
          if (window.location.pathname.startsWith("/admin")) {
            window.location.href = "/admin/login";
          }
          break;
        case 403:
          // Forbidden
          console.error("Access forbidden:", response.data.message);
          break;
        case 404:
          // Not found
          console.error("Resource not found:", response.data.message);
          break;
        case 422:
          // Validation error
          console.error("Validation error:", response.data.errors);
          break;
        case 500:
          // Server error
          console.error("Server error:", response.data.message);
          break;
        default:
          console.error("API error:", response.data.message);
      }
    } else if (error.request) {
      // Network error
      console.error("Network error:", error.message);
    } else {
      // Other error
      console.error("Request error:", error.message);
    }

    return Promise.reject(error);
  },
);

// API endpoints
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  getProfile: () => api.get("/auth/me"),
  updateProfile: (profileData) => api.put("/auth/update-profile", profileData),
  updatePassword: (passwordData) =>
    api.put("/auth/update-password", passwordData),
};

export const productsAPI = {
  getProducts: (params) => api.get("/products", { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  createProduct: (productData) => api.post("/products", productData),
  updateProduct: (id, productData) => api.put(`/products/${id}`, productData),
  deleteProduct: (id) => api.delete(`/products/${id}`),
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/products/bulk-import", formData);
  },
};

export const galleryAPI = {
  getImages: (params) => api.get("/gallery", { params }),
  getCategories: () => api.get("/gallery/categories"),
  uploadImage: (imageData) => api.post("/gallery/upload", imageData),
  updateImage: (id, imageData) => api.put(`/gallery/${id}`, imageData),
  deleteImage: (id) => api.delete(`/gallery/${id}`),
  bulkUpload: (formData) => api.post("/gallery/bulk-upload", formData),
};

export const contactAPI = {
  submitForm: (formData) => api.post("/contact/submit", formData),
  getInquiries: (params) => api.get("/contact/inquiries", { params }),
  getInquiry: (id) => api.get(`/contact/inquiries/${id}`),
  updateStatus: (id, statusData) =>
    api.put(`/contact/inquiries/${id}/status`, statusData),
  respond: (id, response) =>
    api.post(`/contact/inquiries/${id}/respond`, { response }),
  delete: (id) => api.delete(`/contact/inquiries/${id}`),
  getAnalytics: () => api.get("/contact/analytics"),
  addNote: (id, content) =>
    api.post(`/contact/inquiries/${id}/note`, { content }),
};

export const adminAPI = {
  getDashboard: () => api.get("/admin/dashboard"),
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (settings) => api.put("/admin/settings", settings),
  exportData: (type) => api.get(`/admin/export/${type}`),
  getLogs: () => api.get("/admin/logs"),
  clearCache: () => api.post("/admin/cache/clear"),
};

export default api;
